import { h } from "preact";
import { useRef, useState, useEffect } from "preact/hooks";
import { requestPassword } from "../../../../../foundation/api";
import { handleError } from "../../../../../foundation/errors/errors";
import Mailcheck from "../../../../../foundation/mailcheck";
import TextFormField from "../../../../../library/FormField/TextFormField";
import LoadingOverlay from "../../../../../library/LoadingOverlay";
import Button from "../../../../../library/Button";
import TipText from "../TipText/TipText";
import MailcheckSuggestion from "../../types/MailcheckSuggestion";
import FormFieldStatus from "../../types/FormFieldStatus";

import "./style.css";

interface Props {
    forgotPasswordParams?: {
        email?: string;
    };
    onRequestSuccess: () => void;
}

function ForgotPasswordForm({ forgotPasswordParams, onRequestSuccess }: Props) {
    const [handle, setHandle] = useState(forgotPasswordParams?.email || "");
    const [handleStatus, setHandleStatus] = useState<FormFieldStatus>({ type: "empty" });
    const [loading, setLoading] = useState<boolean>(false);
    const handleTimeoutRef = useRef<number>();

    useEffect(() => {
        checkHandleStatus(handle);
    }, []);

    function handleHandleChange(event: Event) {
        const { value } = event.target as HTMLInputElement;
        setHandle(value);
        clearTimeout(handleTimeoutRef.current);
        handleTimeoutRef.current = window.setTimeout(() => checkHandleStatus(value), 1000);
    }

    function checkHandleStatus(value: string) {
        if (!value && handleStatus.type !== "empty") {
            setHandleStatus({ type: "empty" });
            return;
        }

        setHandleStatus({ type: "success" });
    }

    function isFormValid() {
        return !["error", "empty"].includes(handleStatus.type);
    }

    function handleEmailSuggestionClick(event: Event, emailSuggestion: string) {
        event.preventDefault();
        setHandle(emailSuggestion);
        checkHandleStatus(emailSuggestion);
    }

    async function handleFormSubmit(event: Event) {
        event.preventDefault();

        if (!isFormValid()) {
            return;
        }

        try {
            setLoading(true);
            await requestPassword(handle);
            onRequestSuccess();
        } catch (error) {
            handleError(error, {
                clientErrorHandler: function () {
                    if (/@/.test(handle)) {
                        let suggestedEmail = "";
                        Mailcheck.run({
                            email: handle,
                            suggested: (suggestion: MailcheckSuggestion) => {
                                suggestedEmail = suggestion.full;
                            }
                        });

                        setHandleStatus({
                            type: "error",
                            explanation: (
                                <span>
                                    This email is not registered.
                                    {suggestedEmail && (
                                        <span>
                                        Did you mean&nbsp;
                                            {/* eslint-disable-next-line react/jsx-no-bind */}
                                            <a href="#" onClick={ev => handleEmailSuggestionClick(ev, suggestedEmail)}>
                                                {suggestedEmail}
                                            </a>?
                                        </span>
                                    )}
                                </span>
                            )
                        });
                    } else {
                        setHandleStatus({
                            type: "error",
                            explanation: "This username is not registered."
                        });
                    }
                }
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <form class="forgotPasswordForm" onSubmit={handleFormSubmit}>
            {loading && <LoadingOverlay/>}
            <p>No worries, we'll send you an email with instructions to reset your password.</p>
            <TextFormField
                class="forgotPasswordFormField"
                name="Email"
                value={handle}
                placeholder="Work email"
                nativeProps={{
                    autocapitalize: "off",
                    class: "fullNameInput",
                    onInput: handleHandleChange
                }}
                maxLength={254}
                tipText={<TipText type={handleStatus.type} explanation={handleStatus.explanation}/>}
                shouldAlwaysDisplayTipText={true}
                shouldDisplayCountDown={false}
                autoFocus/>
            <Button
                disabled={!isFormValid()}
                class="emailSubmitButton"
                type="submit"
                primary>
                Reset password
            </Button>
        </form>
    );
}

export default ForgotPasswordForm;
